import { useCallback, useEffect, useMemo, useState } from "react";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteWorkOrder,
  fetchCustomerWorkOrders,
  fetchUserWorkOrders,
  fetchWorkOrderStatusCounts,
} from "../../../services/api/workOrder";
import dayjs from "dayjs";
import WorkOrderStatus from "../../../components/WorkOrderStatus";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import FilterChip from "../../../components/FilterChip";
import { debounce, displayFormattedTotalTimelog, navigateWithReload } from "../../../utils/helpers";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseTooltip from "../../../components/ui/BaseTooltip";

import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getTimeZoneAbbreviation } from "../../../utils/getTimeZoneAbbreviation";
import { fullName, joinNonEmpty } from "../../../utils/displayValue";
import { hasSchedule, workOrderLocalDateTime } from "../../../utils/workOrderSchedule";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import BaseTextField from "../../../components/ui/BaseTextField";
import { setClientFilters } from "../../../store/filters/clientFiltersSlice";
import ClientBoardWorkOrderFilters from "./ClientBoardWorkOrderFilters";
import {
  fetchAllSubCategories,
  fetchAllVendors,
  fetchCategories,
} from "../../../services/api/category";
import {
  CanceledByType,
  CANCELLATION_REASON_LABELS,
  CurrencySymbol,
  CUSTOMER_EDITABLE_STATUSES,
  ModuleType,
  PaymentType,
  UserType,
  WorkOrderStatusType,
} from "../../../utils/enum";
import { fetchCompanyUsers } from "../../../services/api/company";
import useAuth from "../../../hooks/useAuth";
import UserProfileLink from "../../../components/ui/UserProfileLink";
import { fetchAllClients } from "../../../services/api/client";
import { fetchAllProjects } from "../../../services/api/project";
import GenerateEngineerInvoiceModal from "../../Invoices/components/GenerateEngineerInvoiceModal";
import { Badge, Menu, MenuItem } from "@mui/material";
import { fetchTeams } from "../../../services/api/teams";

dayjs.extend(utc);
dayjs.extend(timezone);

const ClientWorkOrderTable = ({
  setSelectedWorkOrderIds,
  selectedWorkOrderIds,
  setSelectedEngineer,
  selectedEngineer,
  setSelectedStatus,
  setSelectedCurrency,
  selectedCurrency,
}) => {
  const navigate = useNavigate();
  const { auth, authRole, getPermissionForModule } = useAuth();
  const { hasDeleteAccess, hasUpdateAccess } = getPermissionForModule(
    ModuleType.CLIENT_WORK_ORDERS,
  );
  // The detail page reads `fromCustomerBoard` to show the customer-facing view
  // (customer status, their own rates), so carry it on every link from here.
  const workOrderDetailUrl = (id) =>
    authRole === UserType.CUSTOMER
      ? `/workOrder/details/${id}?fromCustomerBoard=true`
      : `/workOrder/details/${id}`;
  const companyId = auth?.user?.company?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const clientFilters = useSelector((s) => s.clientFilters.workOrders);
  const { page, pageSize } = clientFilters.pagination;
  const defaultFilterParam = searchParams.get("filter") || "";
  const defaultFilterArray = defaultFilterParam.split(",").filter(Boolean);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const [filters, setFilters] = useState(() => {
    if (defaultFilterArray.length > 0) {
      return { status: defaultFilterArray };
    } else {
      return { status: ["PUBLISHED"] };
    }
  });

  const [workOrders, setWorkOrders] = useState([]);
  const search = clientFilters.search;
  const [localSearch, setLocalSearch] = useState(search || "");
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: ["id"],
    right: [],
  });
  const [paginationModel, setPaginationModel] = useState({
    page,
    pageSize,
  });
  const buildStatusForApi = (status) => {
    if (!status) return [];

    const statusArray = Array.isArray(status) ? status : [status];

    if (statusArray.includes("WORKDONE")) {
      return [...new Set([...statusArray, "VERIFIED"])];
    }

    return statusArray;
  };
  const [queryParams, setQueryParams] = useState({
    page: page + 1,
    limit: pageSize,
    status: buildStatusForApi(filters.status),
    search: "",
    sortBy: "orderStartDate,orderStartTime",
    sortOrder: "asc",
    clientIds: [auth?.user?.customer?.id].filter(Boolean),
  });

  const filtersPayload = useMemo(
    () => ({
      categoryId: clientFilters.categoryId || undefined,
      vendorId: clientFilters.vendorId || undefined,
      subCategoryIds: clientFilters.subCategoryIds || undefined,
      teamIds: clientFilters.teamIds || undefined,
      clientIds: clientFilters.clientIds || undefined,
      minRate: clientFilters.minRate || undefined,
      maxRate: clientFilters.maxRate || undefined,
      minRequests: clientFilters.minRequests !== "" ? clientFilters.minRequests : undefined,
      maxRequests: clientFilters.maxRequests !== "" ? clientFilters.maxRequests : undefined,
      paymentType: clientFilters.paymentType || undefined,
      startDate: clientFilters.startDate || undefined,
      endDate: clientFilters.endDate || undefined,

      country: clientFilters.country.label || undefined,
      providerId: clientFilters.provider.value || undefined,
      teamId: clientFilters.teamId || undefined,
      clientId: clientFilters.clientId || undefined,
      projectId: clientFilters.projectId || undefined,
      currency: clientFilters.currency || "",
      engineerCurrency: clientFilters.engineerCurrency || "",
      createdById: clientFilters.createdById || undefined,
      id: clientFilters.id || undefined,
      canceledBy: clientFilters.canceledBy || undefined,
      cancellationReason: clientFilters.cancellationReason || undefined,
    }),
    [clientFilters],
  );
  const {
    data: workOrdersData,
    isLoading: isUserWorkOrderLoading,
    error: isWorkOrderError,
    refetch: refetchUserWorkOrders,
  } = useQuery({
    queryKey: ["userWorkOrders", queryParams, filtersPayload],
    queryFn: () =>
      fetchCustomerWorkOrders({
        ...queryParams,
        ...filtersPayload,
        sortBy: queryParams.sortBy,
        sortOrder: queryParams.sortOrder,
        clientIds: filtersPayload.clientIds?.length
          ? filtersPayload.clientIds
          : queryParams.clientIds,
      }),
  });

  const { data: statusCountData, refetch: refetchStatusCounts } = useQuery({
    queryKey: ["workOrderStatusCounts", filtersPayload, clientFilters.search, "workboard"],
    queryFn: () =>
      fetchWorkOrderStatusCounts({
        ...filtersPayload,
        search: clientFilters.search,
        clientIds: filtersPayload.clientIds?.length
          ? filtersPayload.clientIds
          : queryParams.clientIds,
      }),
  });

  const {
    isLoading: isLoginLoading,
    status,
    error,
    mutateAsync: deleteWorkOrderMutation,
  } = useMutation({
    mutationFn: deleteWorkOrder,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    enabled: isFilterDrawerOpen,
  });

  const { data: vendorsData } = useQuery({
    queryKey: ["vendors"],
    queryFn: fetchAllVendors,
    enabled: isFilterDrawerOpen,
  });

  const { data: subCategoriesData } = useQuery({
    queryKey: ["subCategories"],
    queryFn: fetchAllSubCategories,
    enabled: isFilterDrawerOpen,
  });

  const companyParams = { id: companyId, filter: true };

  const {
    data: companyUsers,
    isPending: isCompanyUsersApiLoading,
    error: isCompanyUsersApiError,
  } = useQuery({
    queryKey: ["companyUsers", companyParams.id, companyParams.filter],
    queryFn: () => fetchCompanyUsers(companyParams),
    enabled: !!companyParams.id && isFilterDrawerOpen,
  });

  const { isPending: isClientsLoading, data: clientsData } = useQuery({
    queryKey: ["clients", companyId, "entities"],
    queryFn: () =>
      fetchAllClients({
        companyId: companyId?.toString(),
        isParentClient: "false",
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const { isPending: isProjectsLoading, data: projectsData } = useQuery({
    queryKey: ["projects", companyId],
    queryFn: () =>
      fetchAllProjects({
        companyId: companyId?.toString(),
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const { isPending: isTeamsLoading, data: teamsData } = useQuery({
    queryKey: ["teams", companyId],
    queryFn: () =>
      fetchTeams({
        companyId: companyId?.toString(),
      }),
    enabled: !!companyId && isFilterDrawerOpen,
  });

  const categories = categoriesData?.data || [];
  const vendors = vendorsData?.data || [];
  const subCategories = subCategoriesData?.data || [];
  const appliedFilters = useMemo(() => {
    const filters = [];
    if (clientFilters?.country?.value) {
      filters.push({
        prefix: "Country",
        value: clientFilters.country.label,
        key: "country",
      });
    }

    if (clientFilters?.provider?.value) {
      filters.push({
        prefix: "Provider",
        value: clientFilters.provider.label,
        key: "provider",
      });
    }
    if (clientFilters?.teamId) {
      const teamName = teamsData?.data?.data?.find((t) => t.id === clientFilters.teamId)?.name;

      if (teamName)
        filters.push({
          prefix: "Team",
          value: teamName,
          key: "teamId",
        });
    }

    if (clientFilters?.clientIds?.length > 0) {
      const items = clientsData?.data?.data
        .filter((s) => clientFilters.clientIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.clientName }));

      items.forEach(({ id, name }) =>
        filters.push({
          prefix: "Client",
          value: name,
          key: "clientIds",
          id,
        }),
      );
    }
    if (clientFilters?.teamIds?.length > 0) {
      const items = teamsData?.data?.data
        .filter((s) => clientFilters.teamIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.name }));

      items.forEach(({ id, name }) =>
        filters.push({
          prefix: "Team",
          value: name,
          key: "teamIds",
          id,
        }),
      );
    }

    if (clientFilters?.projectId) {
      const projectName = projectsData?.data?.projects?.find(
        (c) => c.id === clientFilters.projectId,
      )?.title;
      if (projectName)
        filters.push({
          prefix: "Project",
          value: projectName,
          key: "projectId",
        });
    }
    if (clientFilters?.currency) {
      const currency = clientFilters.currency || "Currency";
      filters.push({
        prefix: "Currency",
        value: currency,
        key: "currency",
      });
    }
    if (clientFilters?.appliedFromDrawer && clientFilters?.status?.length > 0) {
      clientFilters.status.forEach((status) => {
        filters.push({
          prefix: "Status",
          // Display first-letter-capital, rest lowercase (e.g. APPROVED -> Approved).
          // Keep the raw enum in `id` so removal still matches.
          value: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
          key: "status",
          id: status,
        });
      });
    }

    if (clientFilters?.createdById) {
      const name =
        companyUsers?.data?.users?.find((c) => c.id === clientFilters.createdById)?.firstName +
        " " +
        companyUsers?.data?.users?.find((c) => c.id === clientFilters.createdById)?.lastName;
      if (name) filters.push({ prefix: "Created By", value: name, key: "createdById" });
    }

    if (clientFilters?.id) {
      const id = clientFilters.id || "ID";
      filters.push({
        prefix: "ID",
        value: id,
        key: "id",
      });
    }

    if (clientFilters?.categoryId) {
      const name = categories.find((c) => c.id === clientFilters.categoryId)?.category;
      if (name) filters.push({ prefix: "Technology Stack", value: name, key: "categoryId" });
    }

    if (clientFilters?.vendorId) {
      const name = vendors.find((v) => v.id === clientFilters.vendorId)?.name;
      if (name) filters.push({ prefix: "Category", value: name, key: "vendorId" });
    }

    if (clientFilters?.subCategoryIds?.length > 0) {
      const items = subCategories
        .filter((s) => clientFilters.subCategoryIds.includes(s.id))
        .map((s) => ({ id: s.id, name: s.subCategory }));

      items.forEach(({ id, name }) =>
        filters.push({
          prefix: "Skill",
          value: name,
          key: "subCategoryIds",
          id,
        }),
      );
    }

    if (clientFilters?.minRate || clientFilters?.maxRate) {
      const min = Number(clientFilters.minRate ?? 0);
      const max = Number(clientFilters.maxRate ?? 100);
      filters.push({
        prefix: "Rate",
        value: min === max ? `$${min}` : `$${min} - $${max}`,
        key: "rateRange",
      });
    }

    if (clientFilters?.startDate || clientFilters?.endDate) {
      filters.push({
        prefix: "Date",
        value: `${clientFilters.startDate || "Any"} - ${clientFilters.endDate || "Any"}`,
        key: "dateRange",
      });
    }

    if (clientFilters?.paymentType) {
      const label = PaymentType[clientFilters.paymentType] || clientFilters.paymentType;
      filters.push({
        prefix: "Payment",
        value: label,
        key: "paymentType",
      });
    }

    if (clientFilters?.minRequests !== "" || clientFilters?.maxRequests !== "") {
      const min = clientFilters.minRequests ?? 0;
      const max = clientFilters.maxRequests ?? 0;

      filters.push({
        prefix: "Requests",
        value: min === max ? `${min}` : `${min} - ${max}`,
        key: "requestsRange",
      });
    }

    if (clientFilters?.canceledBy) {
      const label = clientFilters.canceledBy === CanceledByType.CLIENT ? "Client" : "Tech";
      filters.push({ prefix: "Cancelled By", value: label, key: "canceledBy" });
    }

    if (clientFilters?.cancellationReason) {
      const label =
        CANCELLATION_REASON_LABELS[clientFilters.cancellationReason] ||
        clientFilters.cancellationReason;
      filters.push({ prefix: "Cancel Reason", value: label, key: "cancellationReason" });
    }

    return filters;
  }, [clientFilters, categories, vendors, subCategories, clientsData, teamsData]);
  const hasAppliedFilters = appliedFilters.length > 0;

  const statusCounts = statusCountData?.statusCounts || {};
  const totalWorkOrdersCount = statusCountData?.totalCount || 0;

  const statusArray = Array.isArray(filters.status)
    ? filters.status
    : filters.status
      ? [filters.status]
      : [];

  const rowCount = (() => {
    const firstStatus = Array.isArray(filters.status) ? filters.status[0] : filters.status;
    const statusArray = Array.isArray(filters.status) ? filters.status : [filters.status];
    const totalCount = workOrdersData?.data?.totalCount || 0;

    // Case 1: search is active OR firstStatus is empty → show total count
    if (search || firstStatus === "" || !firstStatus) {
      return totalCount;
    }

    // Case 2: client drawer filter applied
    if (clientFilters?.appliedFromDrawer) {
      if (statusArray.length === 1) {
        return statusCounts[firstStatus] || 0;
      } else {
        // multiple statuses → treat as all
        return totalCount;
      }
    }

    // Case 3: firstStatus is defined
    if (firstStatus) {
      if (firstStatus === "APPROVED") {
        return totalCount;
      } else {
        return statusCounts[firstStatus] || 0;
      }
    }

    // Fallback: show totalWorkOrdersCount
    return totalWorkOrdersCount;
  })();
  useEffect(() => {
    setLocalSearch(search || "");
  }, [search]);

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      page: page + 1,
      status: buildStatusForApi(
        clientFilters.status?.length ? clientFilters.status : filters.status,
      ),
      search: prev.search,
    }));

    applyFilter();
  }, [filters]);

  useEffect(() => {
    if (clientFilters.status && clientFilters.status.length > 0) {
      setFilters({ status: clientFilters.status });
    } else {
      setFilters({ status: ["PUBLISHED"] });
    }
  }, [clientFilters.status]);

  useEffect(() => {
    if (clientFilters.engineerCurrency) {
      setSelectedCurrency(clientFilters.engineerCurrency);
    }
    if (clientFilters.provider) {
      setSelectedEngineer({
        providerName: clientFilters.provider.label,
        providerId: clientFilters.provider.value,
      });
    }
    if (clientFilters.status && clientFilters.status.length > 0) {
      setSelectedStatus(clientFilters.status);
    }
  }, [clientFilters]);

  useEffect(() => {
    if (filtersChanged) {
      setFiltersChanged(false);
    }
  }, [filtersChanged]);

  useEffect(() => {
    setPaginationModel({ page, pageSize });
    setQueryParams((prev) => ({ ...prev, page: page + 1, limit: pageSize }));
  }, [page, pageSize]);

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length > 0) {
      const sortDirection = newSortModel[0].sort;

      setQueryParams((prev) => ({
        ...prev,
        sortBy: "orderStartDate,orderStartTime",
        sortOrder: sortDirection,
      }));
    } else {
      setQueryParams((prev) => ({
        ...prev,
        sortBy: "orderStartDate,orderStartTime",
        sortOrder: "asc",
      }));
    }
  };

  const applyFilter = () => {
    const allWorkOrders = workOrdersData?.data?.workOrders || [];
    const statusToFilter = clientFilters.status?.length ? clientFilters.status : filters.status;
    if (!filters.status || filters.status.length === 0 || filters.status.includes("")) {
      setWorkOrders(allWorkOrders);
      return;
    }

    if (filters.status.includes("INFLIGHT")) {
      const filtered = filterInFlightWorkOrders();
      setWorkOrders(filtered);
      return;
    }

    const filteredWorkOrders = allWorkOrders.filter((wo) => statusToFilter.includes(wo.status));
    setWorkOrders(filteredWorkOrders);
  };

  const normalizeStatus = (input) => {
    if (Array.isArray(input)) return input;
    if (typeof input === "string") return [input];
    return [""];
  };

  const handleFilterClick = (status) => {
    let statusArray = normalizeStatus(status);

    setFilters({ status: statusArray });

    const queryString = statusArray.filter(Boolean).join(",");
    setSearchParams({ filter: queryString });

    dispatch(
      setClientFilters({
        module: "workOrders",
        filterName: "status",
        data: statusArray,
      }),
    );
    dispatch(
      setClientFilters({
        module: "workOrders",
        filterName: "appliedFromDrawer",
        data: false,
      }),
    );
  };

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedWorkOrder(item);
  };

  const handleClose = () => {
    setContextMenu(null);
    setSelectedWorkOrder(null);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
    dispatch(
      setClientFilters({
        module: "workOrders",
        filterName: "pagination",
        data: {
          page: newPaginationModel.page,
          pageSize: newPaginationModel.pageSize,
        },
      }),
    );
  };

  const convertToLocalTime = (time, timeZone) => {
    if (!timeZone) return;

    const targetTime = dayjs(time).tz(timeZone, true);

    const localTime = targetTime.local().format("hh:mm A");

    return localTime;
  };

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`table-font-size font-normal text-primary cursor-pointer`}
          onClick={() => handleNavigate(params.value)}
        >
          {params.row?.referenceId}
        </p>
      ),
    },
    {
      field: "incidentId",
      headerName: "Incident ID",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`table-font-size truncate max-w-40 overflow-hidden whitespace-nowrap cursor-default`}
        >
          {params.value}
        </p>
      ),
    },

    {
      field: "client",
      width: 200,
      headerName: "Client",
      sortable: false,
      renderCell: (params) => (
        <p className="table-font-size">{params?.row?.client?.clientName || "-"}</p>
      ),
    },
    {
      field: "project",
      width: 200,
      headerName: "Project",
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)}
          className={`cursor-pointer `}
          // onClick={() =>
          //   navigate(`/workOrders/projects/edit/${params?.row?.project?.id}`)
          // }
        >
          {params?.row?.project && params?.row?.project?.title}
        </p>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      // Room for "Submitted -> Counter Received" without clipping the sub-status chip.
      width: 260,
      sortable: false,
      renderCell: (params) => {
        const isCustomer = auth?.user?.role?.role === UserType.CUSTOMER;
        const customerOwnedStatuses = new Set(["APPROVED", "INVOICED", "PAID"]);
        // For a customer, approved-and-after is tracked on customerStatus while the
        // engineer-side `status` stays APPROVED through invoicing/payment. Decide the
        // displayed status per row from its own customerStatus (so it's correct under
        // the ALL filter too), falling back to status for pre-approval orders.
        const useCustomerStatus =
          isCustomer && customerOwnedStatuses.has(params.row?.customerStatus);
        const displayStatus = useCustomerStatus
          ? params.row?.customerStatus
          : params.value === "VERIFIED"
            ? "WORKDONE"
            : params.value;
        return (
          <div className="flex items-center" onContextMenu={(e) => handleContextMenu(e, params)}>
            <WorkOrderStatus
              status={displayStatus}
              isCustomer={isCustomer}
              className="!py-[1.8px] !min-w-[70px] !w-auto !px-2 !text-[11px] whitespace-nowrap"
            />

            {params.value !== "CANCELLED" &&
              params.row?.subStatus &&
              (params.row.subStatus !== WorkOrderStatusType.PO_PENDING ||
                params.row?.purchaseOrderNumber?.trim()?.length === 0) && (
                <>
                  <i className="fa-regular fa-arrow-right-long mx-2 text-primary"></i>

                  <div className="ml-1">
                    <WorkOrderStatus
                      status={params.row.subStatus}
                      className="!py-[1.5px] !min-w-[80px] !w-auto !px-2 whitespace-nowrap !text-[9px]"
                    />
                  </div>
                </>
              )}

            {params.value === "CANCELLED" && params.row?.customerStatus === "CANCELLED_PAYABLE" && (
              <>
                <i className="fa-regular fa-arrow-right-long mx-2 text-primary"></i>

                <div className="ml-1">
                  <WorkOrderStatus
                    status={params.row?.customerStatus}
                    className="!py-[1.5px] !min-w-[80px] !w-auto !px-2 whitespace-nowrap !text-[9px]"
                  />
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params)} // Right-click triggers context menu
          className="table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap text-primary cursor-pointer"
          onClick={() => handleNavigate(params.row?.id)}
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "engineer",
      width: 200,
      headerName: "Engineer",
      sortable: false,
      renderCell: (params) => (
        <div onContextMenu={(e) => handleContextMenu(e, params)}>
          <UserProfileLink userId={params?.value?.id} search="?direct=true">
            {params?.value?.displayName || (params?.value && fullName(params?.value))}
          </UserProfileLink>
          {params?.value?.isVendor && (
            <span className="text-xs text-gray-500 ml-1">ID: {params.value.displayId}</span>
          )}
        </div>
      ),
    },
    {
      field: "orderStartDate",
      headerName: "Schedule",
      width: 200,
      sortable: true,
      renderCell: (params) => (
        <div>
          <p className={`table-font-size`} onContextMenu={(e) => handleContextMenu(e, params)}>
            {/* A draft may have no schedule yet — formatting null here would
                print the literal string "Invalid Date". */}
            {hasSchedule(params?.row?.orderStartDate, params?.row?.orderStartTime) ? (
              <>
                {dayjs(params?.row?.orderStartDate).format("MM/DD/YYYY")}

                <span> at </span>

                {dayjs.utc(params?.row?.orderStartTime).format("hh:mm A")}
                <span> ({getTimeZoneAbbreviation(params?.row?.timeZone) || ""})</span>
              </>
            ) : (
              "-"
            )}
          </p>
        </div>
      ),
    },

    {
      field: "address",
      headerName: "Address",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <div className="cursor-default" onContextMenu={(e) => handleContextMenu(e, params)}>
          <BaseTooltip
            position="top"
            interactive={true}
            content={
              <div className="relative pb-2">
                <p className="text-white">
                  {joinNonEmpty([params?.value?.addressLine1, params?.value?.country])}
                </p>
                {/* <i
                  className="fa-solid fa-diamond-turn-right text-secondary text-xl top-9 right-0 absolute cursor-pointer"
                  onClick={() =>
                    openInGoogleMaps(
                      params?.value?.latitude,
                      params?.value?.longitude
                    )
                  }
                ></i> */}
              </div>
            }
          >
            <p className="table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap">
              {joinNonEmpty([params?.value?.addressLine1, params?.value?.country])}
            </p>
          </BaseTooltip>
        </div>
      ),
    },
    {
      field: "timeLogs",
      width: 200,
      headerName: "Check In",
      sortable: false,
      renderCell: (params) => (
        <p className={``} onContextMenu={(e) => handleContextMenu(e, params)}>
          {params?.value[0]?.checkInTime &&
            `${dayjs(params?.value[0]?.checkInTime).tz(params?.row?.timeZone).format("h:mm A")}`}
        </p>
      ),
    },
    {
      field: "timeLogs1",
      width: 200,
      headerName: "Check Out",
      sortable: false,
      renderCell: (params) => (
        <p className={``} onContextMenu={(e) => handleContextMenu(e, params)}>
          {params?.row?.timeLogs[params.row?.timeLogs.length - 1]?.checkOutTime &&
            `${dayjs(params?.row?.timeLogs[params.row?.timeLogs.length - 1]?.checkOutTime)
              .tz(params?.row?.timeZone)
              .format("h:mm A")}`}
        </p>
      ),
    },
    {
      field: "engineerHoursWorked",
      width: 200,
      headerName: "Hours Logged",
      sortable: false,
      renderCell: (params) => (
        // <p className={``}>{params?.value?.toFixed(2) || 0.0} </p>
        <p className={``} onContextMenu={(e) => handleContextMenu(e, params)}>
          {params?.value !== undefined && params?.value !== null && params?.value !== 0
            ? displayFormattedTotalTimelog(params.value)
            : ""}
        </p>
      ),
    },
    {
      field: "paymentType",
      headerName: "Billing Rates",
      width: 250,
      sortable: false,
      renderCell: (params) => {
        return (
          <div
            onContextMenu={(e) => handleContextMenu(e, params)}
            className="cursor-pointer"
            // onClick={() => navigate(`/workOrder/payment/${params.row?.id}`)}
          >
            {params.row?.paymentType === "FTH" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FTH}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.fthRate?.toFixed(2)} &
                Additional hrs @ {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "CUSTOM_HOURS" && (
              <p className="table-font-size">
                <span className="text-primary">
                  Fixed Hours (
                  {/* Blank rather than "undefined hour" when the draft has not
                      set the hours yet. */}
                  {params.row?.customHours
                    ? `${params.row.customHours} ${params.row.customHours > 1 ? "hours" : "hour"}`
                    : ""}
                  )
                </span>{" "}
                @ {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.customHoursRate?.toFixed(2)} & Additional hrs @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "HOURLY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.HOURLY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.hourlyRate?.toFixed(2)}{" "}
                (min {params.row.minimumHours} hrs)
              </p>
            )}
            {params.row?.paymentType === "HALF_DAY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.HALF_DAY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params.row?.halfDayRate?.toFixed(2)} &
                Additional hrs @ {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "FULL_DAY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FULL_DAY}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params?.row?.fullDayRate?.toFixed(2)}{" "}
                & Additional hrs @ {CurrencySymbol[params.row?.currency] || ""}{" "}
                {params.row?.hourlyRate?.toFixed(2)}
              </p>
            )}
            {params.row?.paymentType === "FIXED" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FIXED}</span> @{" "}
                {CurrencySymbol[params.row?.currency] || ""} {params?.row?.fixedAmount?.toFixed(2)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      field: "purchaseOrders",
      headerName: "Purchase Order",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const { purchaseOrders, purchaseOrderNumber } = params.row;

        if (purchaseOrderNumber && (!purchaseOrders || purchaseOrders.length === 0)) {
          return <p className="cursor-default">{purchaseOrderNumber}</p>;
        }

        if (purchaseOrders?.length) {
          return (
            <div
              className="truncate w-full text-ellipsis whitespace-nowrap"
              title={purchaseOrders.map((po) => po.number).join(", ")}
            >
              <p className="truncate w-full text-ellipsis whitespace-nowrap">
                {purchaseOrders.map((po, index) => (
                  <span key={po.id}>
                    {po.attachment?.url ? (
                      <a
                        href={po.attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary cursor-pointer hover:underline"
                      >
                        {po.number}
                      </a>
                    ) : (
                      <span className="cursor-default">{po.number}</span>
                    )}
                    {index !== purchaseOrders.length - 1 && ", "}
                  </span>
                ))}
              </p>
            </div>
          );
        }

        return <span>-</span>;
      },
    },
    {
      field: "localTime",
      headerName: "Local Time",
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const userTimeZone = dayjs.tz.guess();

        // Null-safe: a draft may have no schedule yet, and dayjs.tz() throws on
        // the "Invalid Date" string rather than returning an invalid dayjs.
        const convertedDateTime = workOrderLocalDateTime(
          params?.row?.orderStartDate,
          params?.row?.orderStartTime,
          params?.row?.timeZone,
        );
        return (
          <div onContextMenu={(e) => handleContextMenu(e, params)}>
            <p className="table-font-size">
              {convertedDateTime ? (
                <>
                  {convertedDateTime.format("MM/DD/YYYY")}
                  <span> at </span>
                  {convertedDateTime.format("hh:mm A")}
                  <span> ({getTimeZoneAbbreviation(userTimeZone)})</span>
                </>
              ) : (
                "-"
              )}
            </p>
          </div>
        );
      },
    },

    {
      field: "workOrderManager",
      headerName: "Manager",
      width: 200,
      sortable: false,
      renderCell: (params) =>
        params?.value && (
          <div onContextMenu={(e) => handleContextMenu(e, params)}>
            <UserProfileLink userId={params?.value?.id}>{fullName(params?.value)}</UserProfileLink>
          </div>
        ),
    },

    {
      field: "locationName",
      width: 200,
      headerName: "Location Name",
      sortable: false,
      renderCell: (params) => (
        <p
          className={`table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap`}
          onContextMenu={(e) => handleContextMenu(e, params)}
        >
          {params?.row?.address?.displayName}
        </p>
      ),
    },
    {
      field: "category",
      width: 200,
      headerName: "Type of Work",
      sortable: false,
      renderCell: (params) => (
        <p
          className="table-font-size"
          onContextMenu={(e) => handleContextMenu(e, params)}
        >{`${params?.value?.category || "not specified"}`}</p>
      ),
    },
    {
      field: "createdBy",
      width: 200,
      headerName: "Created By",
      sortable: false,
      renderCell: (params) => (
        <div onContextMenu={(e) => handleContextMenu(e, params)}>
          <UserProfileLink userId={params?.value?.id}>{fullName(params?.value)}</UserProfileLink>
        </div>
      ),
    },
    {
      field: "cancelledWorkOrder",
      width: 150,
      headerName: "Cancelled By",
      sortable: false,
      renderCell: (params) => {
        const cancellation = params?.value?.[0];
        if (!cancellation?.canceledBy) return null;
        const label = cancellation.canceledBy === CanceledByType.CLIENT ? "Client" : "Tech";
        const reasonKey = cancellation.cancellationReason;
        const reasonText = reasonKey ? CANCELLATION_REASON_LABELS[reasonKey] || reasonKey : null;
        const customReason = cancellation.reason;
        const tooltipContent = (
          <div>
            {reasonText && <p className="text-white">{reasonText}</p>}
            {customReason && <p className="text-white text-xs mt-1">{customReason}</p>}
          </div>
        );
        return (
          <div onContextMenu={(e) => handleContextMenu(e, params)}>
            {reasonText ? (
              <BaseTooltip position="top" content={tooltipContent}>
                <span className="table-font-size cursor-default">{label}</span>
              </BaseTooltip>
            ) : (
              <span className="table-font-size">{label}</span>
            )}
          </div>
        );
      },
    },
    {
      field: "notes",
      width: 200,
      headerName: "Notes",
      sortable: false,
      renderCell: (params) => (
        <div className="cursor-default">
          <BaseTooltip
            position="top"
            interactive={true}
            content={<p className="text-white">{params.value}</p>}
          >
            <p className="table-font-size truncate max-w-72 overflow-hidden whitespace-nowrap">
              {params.value}
            </p>
          </BaseTooltip>
        </div>
      ),
    },
    {
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        // This listing is already scoped to the customer's own clients, so every row
        // here is one they may edit — the API re-checks the same scope.
        //
        // This board is the CLIENT_WORK_ORDERS route, and only the Customer
        // designation is granted that module — clients work from WORK_ORDERS
        // (WorkOrderTable). So the rule here is the customer one: the permission
        // decides whether they may delete at all, then it is narrowed to drafts
        // they raised themselves. A draft a client wrote for them is not theirs
        // to discard. remove() enforces the same rules server-side.
        const isDraftRow = params?.row?.status === WorkOrderStatusType.DRAFT;
        const isMine = params?.row?.createdBy?.id === auth?.user?.id;
        const canDelete = hasDeleteAccess && isDraftRow && isMine;

        // Editing follows the same window as the detail page (DetailsTab):
        // Draft, Submitted and Accepted only. Once an engineer is assigned the
        // work is under way and changes are the client's to make.
        const canEdit = hasUpdateAccess && CUSTOMER_EDITABLE_STATUSES.includes(params?.row?.status);

        const menuOptions = [
          ...(canEdit ? [{ label: "Edit", value: "EDIT" }] : []),
          ...(canDelete ? [{ label: "Delete", value: "DELETE" }] : []),
        ];

        if (!menuOptions.length) return null;

        return (
          <BaseDropdownV2
            menuOptions={menuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(value) => handleItemClick(value, params?.row)}
          />
        );
      },
      pinRight: true,
    },
  ];

  const handleItemClick = (value, params) => {
    if (value === "EDIT") {
      navigate(`/workOrder/edit/summary/${params?.id}`);
    }
    if (value === "DELETE") {
      handleDeleteWorkOrder(params?.id);
    }
  };

  const handleNavigate = (id) => {
    navigate(workOrderDetailUrl(id));
  };

  //this will initially run to set the work orders data fetched from API
  useEffect(() => {
    setWorkOrders(workOrdersData?.data.workOrders);
    applyFilter();
  }, [workOrdersData]);

  //this is filter the work order based on current filter selected without API call
  useEffect(() => {
    if (!filters.status || filters.status.length === 0 || filters.status.includes("")) {
      setWorkOrders(workOrdersData?.data.workOrders);
    } else if (filters.status.includes("INFLIGHT")) {
      const filteredWorkOrders = filterInFlightWorkOrders();
      setWorkOrders(filteredWorkOrders);
    } else {
      const isCustomer = auth?.user?.role?.role === "CUSTOMER";
      const customerOwnedStatuses = new Set(["APPROVED", "INVOICED", "PAID"]);
      const filteredWorkOrders = workOrdersData?.data.workOrders.filter((workOrder) => {
        const useCustomerStatus =
          isCustomer && filters.status.every((s) => customerOwnedStatuses.has(s));
        const woStatus = useCustomerStatus ? workOrder.customerStatus : workOrder.status;
        return filters.status.includes(woStatus);
      });
      setWorkOrders(filteredWorkOrders);
    }
  }, [filters]);

  const filterInFlightWorkOrders = () => {
    const today = dayjs().startOf("day");
    const tomorrow = today.add(1, "day");

    const filteredWorkOrders = workOrdersData?.data.workOrders.filter((workOrder) => {
      const workOrderDate = dayjs(workOrder.orderStartDate).startOf("day");
      return workOrderDate.isSame(today, "day") || workOrderDate.isSame(tomorrow, "day");
    });

    return filteredWorkOrders;
  };

  const toggleDropdown = (id) => {
    if (openDropdownId === id) {
      setOpenDropdownId(null); // Close the dropdown if it's already open
    } else {
      setOpenDropdownId(id); // Open the dropdown for the selected row
    }
  };

  const handleDeleteWorkOrder = async (workOrderId) => {
    try {
      const payload = {
        id: workOrderId,
      };
      const response = await deleteWorkOrderMutation(payload);
      setOpenDropdownId(null); // Close the dropdown if it's already open
      dispatch(
        showToast({
          message: "Work Order deleted successfully",
          type: "success",
        }),
      );
      refetchUserWorkOrders();
      refetchStatusCounts();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };
  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "search",
            data: val,
          }),
        );
      }, 500),
    [dispatch],
  );
  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      search: search || "",
      page: 1,
    }));
  }, [search]);

  const handleSearchChange = (val) => {
    setLocalSearch(val);
    debouncedSearch(val);
  };

  const handleRemoveFilter = (key, id) => {
    if (key === "rateRange") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "minRate",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "maxRate",
          data: "",
        }),
      );
    } else if (key === "requestsRange") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "minRequests",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "maxRequests",
          data: "",
        }),
      );
    } else if (key === "dateRange") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "startDate",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "endDate",
          data: "",
        }),
      );
    } else if (["country", "provider", "projectId", "createdById", "id", "teamId"].includes(key)) {
      if (key === "provider") {
        setSelectedEngineer(null);
      }
      dispatch(setClientFilters({ module: "workOrders", filterName: key, data: "" }));
    } else if (key === "clientIds") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "clientIds",
          data: clientFilters.clientIds.filter((clientId) => clientId !== id),
        }),
      );
      const selectedProject = projectsData?.data?.projects?.find(
        (p) => p.id === clientFilters.projectId,
      );

      if (selectedProject?.client?.id === id) {
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "projectId",
            data: null,
          }),
        );
      }
    } else if (key === "subCategoryIds") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "subCategoryIds",
          data: clientFilters.subCategoryIds.filter((subId) => subId !== id),
        }),
      );
    } else if (key === "vendorId") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "vendorId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "subCategoryIds",
          data: [],
        }),
      );
    } else if (key === "categoryId") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "categoryId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "vendorId",
          data: "",
        }),
      );
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "subCategoryIds",
          data: [],
        }),
      );
    } else if (key === "teamIds") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "teamIds",
          data: clientFilters.teamIds.filter((teamId) => teamId !== id),
        }),
      );
    } else if (key === "engineerCurrency") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "engineerCurrency",
          data: "",
        }),
      );

      setSelectedCurrency(null);
    } else if (key === "canceledBy") {
      dispatch(setClientFilters({ module: "workOrders", filterName: "canceledBy", data: "" }));
      dispatch(
        setClientFilters({ module: "workOrders", filterName: "cancellationReason", data: "" }),
      );
    } else {
      dispatch(setClientFilters({ module: "workOrders", filterName: key, data: "" }));
    }

    dispatch(
      setClientFilters({
        module: "workOrders",
        filterName: "pagination",
        data: { page: 0, pageSize: clientFilters.pagination.pageSize },
      }),
    );
    if (key === "status") {
      dispatch(
        setClientFilters({
          module: "workOrders",
          filterName: "status",
          data: clientFilters.status.filter((s) => s !== id),
        }),
      );

      if (clientFilters.status.length === 1) {
        dispatch(
          setClientFilters({
            module: "workOrders",
            filterName: "appliedFromDrawer",
            data: false,
          }),
        );
      }
    }

    setFiltersChanged(true);
  };
  /**
   * The All chip always shows the grand total, whatever tab is active — that is
   * what makes it "All". `totalCount` here is statusCountData.totalCount, which
   * the API computes without the status filter (it does honour the drawer
   * filters), so it is already the right number.
   *
   * This used to branch per status and return the selected status's own count
   * for anything outside a hardcoded list — which meant clicking Draft or
   * Submitted made the All chip echo that tab's number instead of the total.
   */
  function getAllChipCount(filtersStatus, statusCounts, totalCount) {
    return totalCount;
  }
  const isSingleStatusActive = (status) => {
    return (
      Array.isArray(filters.status) && filters.status.length === 1 && filters.status[0] === status
    );
  };

  const isMultipleStatusSelected = () => {
    return Array.isArray(filters.status) && filters.status.length > 1;
  };

  const isAllSelected = () => {
    return (
      !filters.status ||
      filters.status.length === 0 ||
      filters.status.includes("") ||
      isMultipleStatusSelected()
    );
  };

  return (
    <>
      {isUserWorkOrderLoading && <BaseLoader isLoading={isUserWorkOrderLoading} />}
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="flex items-center border-r border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>

            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => {
                  return (
                    <div
                      key={(f.key || "") + (f.value || "") + (f.id || "")}
                      className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                    >
                      <span className="text-primary">{f.prefix}:</span>&nbsp;
                      <p className="text-primary text-sm mx-1">{f.value}</p>
                      <button
                        onClick={() => handleRemoveFilter(f.key, f.id)}
                        className="ml-2 text-primary hover:text-red-500"
                        aria-label={`Remove ${f.prefix} ${f.value}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2 shrink-0">
              <BaseTextField
                placeholder="Search by WO title or reference ID"
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                value={localSearch}
                startAdornment={<i className="fa-light fa-magnifying-glass text-lg" />}
                className="w-full sm:w-[300px] lg:w-[350px]"
              />
            </div>
          </div>
        </div>

        <div
          className=" my-4
            flex
            overflow-x-auto
            scrollbar-thin
            scrollbar-thumb-gray-400
            scrollbar-track-gray-200
            [&::-webkit-scrollbar]:h-2
            [&::-webkit-scrollbar-thumb]:rounded
            [&::-webkit-scrollbar-thumb]:bg-gray-400
            [&::-webkit-scrollbar-track]:bg-gray-200
          "
        >
          <div className="mr-5">
            <FilterChip
              filter="Draft"
              count={
                search && filters.status.includes("DRAFT")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.DRAFT || 0
              }
              active={isSingleStatusActive("DRAFT")}
              onClick={() => handleFilterClick(["DRAFT"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Submitted"
              count={
                search && filters.status.includes("PENDING_APPROVAL")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.PENDING_APPROVAL || 0
              }
              active={isSingleStatusActive("PENDING_APPROVAL")}
              onClick={() => handleFilterClick(["PENDING_APPROVAL"])}
            />
          </div>
          <div className="mr-5">
            {/* Customers see a published work order as "Accepted" — from their side
                the client accepted the ticket they submitted. */}
            <FilterChip
              filter="Accepted"
              count={
                search && filters.status.includes("PUBLISHED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.PUBLISHED || 0
              }
              active={isSingleStatusActive("PUBLISHED")}
              onClick={() => handleFilterClick(["PUBLISHED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Assigned"
              count={
                search && filters.status.includes("ASSIGNED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.ASSIGNED || 0
              }
              active={isSingleStatusActive("ASSIGNED")}
              onClick={() => handleFilterClick(["ASSIGNED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Inprogress"
              count={
                search && filters.status.includes("INPROGRESS")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.INPROGRESS || 0
              }
              active={isSingleStatusActive("INPROGRESS")}
              onClick={() => handleFilterClick(["INPROGRESS"])}
            />
          </div>

          <div className="mr-5">
            <FilterChip
              filter="Work Done"
              count={
                search && filters.status.includes("WORKDONE")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.WORKDONE || 0 + statusCounts.VERIFIED || 0
              }
              active={isSingleStatusActive("WORKDONE")}
              onClick={() => handleFilterClick(["WORKDONE"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Approved"
              count={
                search && filters.status.includes("APPROVED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts?.APPROVED || 0 || 0
              }
              active={isSingleStatusActive("APPROVED")}
              onClick={() => handleFilterClick(["APPROVED"])}
            />
          </div>

          <div className="mr-5">
            <FilterChip
              filter="Invoiced"
              count={
                search && filters.status.includes("INVOICED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.INVOICED || 0
              }
              active={isSingleStatusActive("INVOICED")}
              onClick={() => handleFilterClick(["INVOICED"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Paid"
              count={
                search && filters.status.includes("PAID")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.PAID || 0
              }
              active={isSingleStatusActive("PAID")}
              onClick={() => handleFilterClick(["PAID"])}
            />
          </div>
          <div className="mr-5">
            <FilterChip
              filter="Cancelled"
              count={
                search && filters.status.includes("CANCELLED")
                  ? workOrdersData?.data?.totalCount
                  : statusCounts.CANCELLED || 0
              }
              active={isSingleStatusActive("CANCELLED")}
              onClick={() => handleFilterClick(["CANCELLED"])}
            />
          </div>

          <FilterChip
            filter="All"
            count={getAllChipCount(filters.status, statusCounts, totalWorkOrdersCount)}
            active={isAllSelected()}
            onClick={() => handleFilterClick("")}
          />
        </div>

        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)] "
          rowHeight={38}
          columns={columns}
          data={workOrdersData?.data?.workOrders || []}
          isLoading={false}
          disableColumnResize={false}
          onRowSelectionModelChange={(workOrderIds) => {
            setSelectedWorkOrderIds(workOrderIds);
          }}
          disableColumnSorting={false}
          sortingMode="server"
          sortModel={
            queryParams.sortBy && queryParams.sortOrder
              ? [{ field: "orderStartDate", sort: queryParams.sortOrder }]
              : []
          }
          onSortModelChange={handleSortModelChange}
          sortingOrder={["asc", "desc"]}
          checkboxSelection
          hideFooter={false}
          pagination
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          paginationMode="server"
        />
        {isFilterDrawerOpen && (
          <ClientBoardWorkOrderFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchCustomerWorkOrders={fetchCustomerWorkOrders}
            currentStatus={filters.status}
            refetchStatusCounts={refetchStatusCounts}
            isWorkOrderLoading={isUserWorkOrderLoading}
            setFiltersChanged={setFiltersChanged}
            hasAppliedFilters={hasAppliedFilters}
            appliedFilters={appliedFilters}
            projectsData={projectsData}
            teamsData={teamsData}
            companyUsers={companyUsers}
          />
        )}
        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
          }
        >
          <MenuItem
            onClick={() => {
              handleNavigate(selectedWorkOrder.id);
              handleClose();
            }}
          >
            <p>Open</p>
          </MenuItem>
          <MenuItem
            onClick={() => {
              window.open(workOrderDetailUrl(selectedWorkOrder.id), "_blank");
              handleClose();
            }}
          >
            <p>Open in New Tab</p>
          </MenuItem>
        </Menu>
      </BaseWrapper>
    </>
  );
};

export default ClientWorkOrderTable;
